export interface Employee {
  id: string;
  name: string;
  email?: string;
  roleId: string;
  roleTitle?: string;
  /** All role IDs assigned (for multi-role). Used for allocation role filter and project PM/AM filter. */
  roleIds?: string[];
  roleTitles?: string[];
  designationId?: string;
  designationName?: string;
  categoryId?: string;
  categoryName?: string;
  serviceGroupId: string;
  serviceGroupName?: string;
  /** All service group IDs (multi-select). Primary/first is serviceGroupId. */
  serviceGroupIds?: string[];
  serviceGroupNames?: string[];
  isActive: boolean;
  /**
   * Effective last working day, 'YYYY-MM-DD'. Set when the employee is deactivated and
   * cleared when reactivated, so it never disagrees with isActive. Offboarding fills it
   * from the record's resign date, not the checklist completion date.
   */
  inactiveDate?: string;
  excludeFromAllocation: boolean;
  excludeFromForecast: boolean;
  excludeFromTimesheet: boolean;
  /** Keeps someone off the 12-month Future Allocation planner. */
  excludeFromFutureAllocation: boolean;

  // Onboarding personal fields
  firstName?: string;
  idNumber?: string;
  mobileNumber?: string;
  residentialAddress?: string;
  currentAddress?: string;
  personalEmail?: string;
  linkedInUrl?: string;
  employmentType?: string;
  /** Bistec Apex Entra ID group, e.g. 'Software_Bistec Apex'. See ENTRA_ID_GROUPS. */
  entraIdGroup?: string;
  headOfDepartmentId?: string;
  headOfDepartmentName?: string;
  joiningDate?: string;
  emergencyContactName?: string;
  emergencyContactNumber?: string;
  cvAttachmentUrl?: string;
}

/**
 * Bistec Apex Entra ID groups selectable on the employee form.
 * The stored value keeps the underscore so it matches the group name in Entra ID
 * exactly; the label replaces it with a space purely for readability.
 */
export const ENTRA_ID_GROUP_OPTIONS: { label: string; value: string }[] = [
  { label: 'Software Bistec Apex', value: 'Software_Bistec Apex' },
  { label: 'Accounting Bistec Apex', value: 'Accounting_Bistec Apex' },
  { label: 'Marketing Bistec Apex', value: 'Marketing_Bistec Apex' },
  { label: 'IT Bistec Apex', value: 'IT_Bistec Apex' },
  { label: 'P&C Bistec Apex', value: 'P&C_Bistec Apex' },
  { label: 'Sales Bistec Apex', value: 'Sales_Bistec Apex' },
  { label: 'L&D Bistec Apex', value: 'Learning & Development_Bistec Apex' },
  { label: 'Admin Bistec Apex', value: 'Admin_Bistec Apex' },
  { label: 'PM Bistec Apex', value: 'PM_Bistec Apex' },
  { label: 'Finance Bistec Apex', value: 'Finance_Bistec Apex' }
];

/** Display form of a stored Entra ID group value (falls back to the raw value). */
export function entraIdGroupLabel(value?: string): string {
  if (!value) return '';
  return ENTRA_ID_GROUP_OPTIONS.find(o => o.value === value)?.label ?? value;
}

export enum EmployeeRole {
  Dev = 1,
  QA = 2,
  ProjectManager = 3,
  BA = 4,
  DevIntern = 5,
  QAIntern = 6,
  DevOpsIntern = 7,
  SeniorProjectManager = 8,
  DataIntern = 9,
  BAIntern = 10,
}

